import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { NavLink as RouterLink } from "react-router-dom";
import { FaHome, FaInfoCircle, FaPhone, FaCogs, FaBriefcase } from "react-icons/fa"; // Importing icons

const GlobalStyle = createGlobalStyle`
  body, html {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    width: 100%;
    font-family: sans-serif;
  }
`;

const Topbar = styled.div`
  background-color: #F19F4D;
  color: white;
  padding: 5px 5px;
  text-align: center;
  font-size: 1.2rem;
  display: none;

  @media (min-width: 768px) {
    display: ${({ show }) => (show ? "block" : "none")};
  }

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  box-sizing: border-box;
`;

const CloseButton = styled.span`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.5rem;
  cursor: pointer;
`;

const Navbar = styled.nav`
  background: white;
  position: fixed;
  top: ${({ showTopbar }) => (showTopbar ? "25px" : "0")};
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 15px;
  z-index: 11;
  box-sizing: border-box;
  transition: top 0.3s ease;
`;

const Logo = styled.img`
  height: 60px;

  @media (max-width: 768px) {
    height: 40px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 40px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(RouterLink)`
  color: #000;
  text-decoration: none;
  font-size: 1.25rem;

  &.active {
    font-weight: bold;
    color: #F19F4D;
  }

  &:hover {
    color: #F19F4D;
  }
`;

const LoginButton = styled.button`
  background-color: #F19F4D;
  color: white;
  padding: 8px 20px;
  font-size: 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: #d88332;
  }
`;

const HamburgerIcon = styled.div`
  display: none;
  cursor: pointer;
  flex-direction: column;
  gap: 5px; /* Increased gap for visibility */
  
  & div {
    width: 35px; /* Increased width for better visibility */
    height: 5px; /* Increased height */
    background-color: #000;
    border-radius: 5px;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const SidebarOverlay = styled.div`
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  position: fixed;
  top: ${({ showTopbar }) => (showTopbar ? "0" : "0")};
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(5px);
  z-index: 99;
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 70%; /* Reduced width of sidebar */
  height: 100%;
  background: white;
  padding: 20px;
  z-index: 100;
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  flex-direction: column;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-in-out forwards;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0%);
    }
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const CloseSidebarButton = styled.span`
  align-self: flex-end;
  font-size: 2rem;
  cursor: pointer;
  margin-bottom: 20px;
`;

const SidebarLink = styled(RouterLink)`
  padding: 15px 0; /* Increased padding for better spacing */
  font-size: 1.5rem; /* Increased font size for visibility */
  text-decoration: none;
  color: #000;
  display: flex;
  align-items: center;
  margin: 10px 0; /* Added margin for spacing */
  border-radius: 5px; /* Slightly rounded corners for visual appeal */

  &:hover {
    color: #0cf;
    background-color: #f0f0f0; /* Light background on hover */
  }

  & svg {
    margin-right: 15px; /* Space between icon and text */
    font-size: 1.8rem; /* Increased icon size */
  }
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scroll, setScroll] = useState(false);
  const [showTopbar, setShowTopbar] = useState(true);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleScroll = () => {
    if (window.scrollY > 50) {
      setScroll(true);
    } else {
      setScroll(false);
    }
  };

  const closeTopbar = () => {
    setShowTopbar(false);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <GlobalStyle />
      <Topbar show={showTopbar}>
        Under construction! Some features aren't working yet, but we're building something great—stay tuned!
        <CloseButton onClick={closeTopbar}>&times;</CloseButton>
      </Topbar>

      <Navbar showTopbar={showTopbar && window.innerWidth > 768}>
        <Logo src="/logo.png" alt="Logo" />
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <NavLinks>
            <NavLink to="/" end>Home</NavLink>
            <NavLink to="/about">About Us</NavLink>
            <NavLink to="/service">Service</NavLink>
            <NavLink to="/career">Career</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </NavLinks>
          <RouterLink to="/Login">
            <LoginButton>Login</LoginButton>
          </RouterLink>
        </div>
        <HamburgerIcon onClick={toggleMenu}>
          <div></div>
          <div></div>
          <div></div>
        </HamburgerIcon>
      </Navbar>

      {/* Blur Background */}
      <SidebarOverlay isOpen={isOpen} showTopbar={showTopbar} onClick={closeSidebar} />

      {/* Sidebar */}
      <Sidebar isOpen={isOpen} showTopbar={showTopbar}>
        <CloseSidebarButton onClick={toggleMenu}>&times;</CloseSidebarButton>
        <SidebarLink to="/" end onClick={toggleMenu}>
          <FaHome /> Home
        </SidebarLink>
        <SidebarLink to="/about" onClick={toggleMenu}>
          <FaInfoCircle /> About Us
        </SidebarLink>
        <SidebarLink to="/service" onClick={toggleMenu}>
          <FaCogs /> Service
        </SidebarLink>
        <SidebarLink to="/career" onClick={toggleMenu}>
          <FaBriefcase /> Career
        </SidebarLink>
        <SidebarLink to="/contact" onClick={toggleMenu}>
          <FaPhone /> Contact
        </SidebarLink>
      </Sidebar>
    </>
  );
};

export default Header;
